import { useRef, useState } from 'react';
import { Button, Divider, Popover, Stack } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format } from 'date-fns';
import { enGB } from 'date-fns/locale';

interface DateRangeControlProps {
  from: string;
  to: string;
  onChange: (from: string, to: string) => void;
}

const MS_DAY = 86_400_000;

const PRESETS = [
  { label: 'Last 2 weeks', days: 13 },
  { label: 'Last 30 days', days: 29 },
  { label: 'Last 90 days', days: 89 },
];

function rangeLabel(from: string, to: string): string {
  const f = new Date(from);
  const t = new Date(to);
  const sameYear = f.getFullYear() === t.getFullYear();
  return `${format(f, sameYear ? 'd MMM' : 'd MMM yyyy')} – ${format(t, 'd MMM yyyy')}`;
}

export function DateRangeControl({
  from,
  to,
  onChange,
}: DateRangeControlProps) {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  const applyPreset = (days: number) => {
    const t = new Date();
    const f = new Date(Date.now() - days * MS_DAY);
    onChange(f.toISOString(), t.toISOString());
  };

  return (
    <>
      <Button
        ref={anchorRef}
        variant="text"
        size="small"
        endIcon={<KeyboardArrowDownIcon />}
        onClick={() => setOpen(true)}
        sx={{ color: 'text.secondary', textTransform: 'none' }}
        data-testid="activity-date-range"
      >
        {rangeLabel(from, to)}
      </Button>
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: { sx: { mt: 1, p: 2, width: 300, borderRadius: 2 } },
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {PRESETS.map(p => (
                <Button
                  key={p.label}
                  size="small"
                  variant="outlined"
                  onClick={() => applyPreset(p.days)}
                  sx={{ textTransform: 'none' }}
                >
                  {p.label}
                </Button>
              ))}
            </Stack>
            <Divider />
            <DatePicker
              label="From"
              value={new Date(from)}
              onChange={d => d && onChange(d.toISOString(), to)}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
            <DatePicker
              label="To"
              value={new Date(to)}
              onChange={d => d && onChange(from, d.toISOString())}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
          </Stack>
        </LocalizationProvider>
      </Popover>
    </>
  );
}
